import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router';
import { Waypoint } from 'react-waypoint';
import { toast } from 'sonner';
import { useErrorMessages, useFullscreenModal, useUserSession } from '../../../models/hooks/appStateHooks';
import { getPrincipalInitials } from '../../../models/NameUtils';
import { getDisplayNameForBulkEmail } from '../../../models/UiUtils';
import { isFilesFeatureEnabled } from '../../../models/utils/accountUtils';
import { invalidateAllAutomationTemplateIdQueries, useAutomationTemplateIdQuery } from '../../../queries';
import { useTextingCampaignDraftUpdateMutation } from '../../../queries/AutomationTemplate/useTextingCampaignDraftUpdateMutation';
import { AdvancedFiltersContextProvider } from '../../components-v2/AdvancedFilters/AdvancedFiltersContext';
import { Button } from '../../components-v2/Button';
import { AdvancedFiltersLegacyUI } from '../../components/AdvancedFilters/AdvancedFiltersLegacyUI';
import { AutoCompleteSearchField } from '../../components/autocomplete/AutoCompleteSearchField';
import { Avatar } from '@HoudiniUI/Avatar';
import { Checkbox } from '../../components/Checkbox';
import { ContactsTableActiveSearchesList } from '../../components/contacts/ContactsTableActiveSearchesList';
import { DeprecatedCloseButton } from '../../components/DeprecatedCloseButton';
import { FlyoutModal } from '../../components/FlyoutModal';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { MultiContainerHeader } from '../../components/MultiContainerHeader';
import { OverflowAccordion } from '../../components/OverflowAccordion';
import { OwnershipFilterDropdown } from '../../components/OwnershipFilterDropdown';
import { SendFromDropdown } from '../../components/SendFromDropdown';
import { DisclosureIcon } from '../../components/svgs/icons/DisclosureIcon';
import { EditItemPenIcon } from '../../components/svgs/icons/EditItemPenIcon';
import { InfoIcon } from '../../components/svgs/icons/InfoIcon';
import { SearchIcon } from '../../components/svgs/icons/SearchIcon';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../components/Tooltip';
import { brandSecondary, successGreen } from '../../styles/colors';
import { baseStyleSheet as bs } from '../../styles/styles';
import { CampaignComposerBaseLayout } from '../BulkHwcPages/presentation';
import { useTextingCampaignEditor } from './hooks/useTextingCampaignEditor';
import { useTextingCampaignRecipientFilter } from './hooks/useTextingCampaignRecipientFilter';
import { PhoneMissingPill } from './PhoneMissingPill';
import { styleSheet } from './styles';
import { getFileSendBlockingError } from './textCampaignComposerContent';
import { TextCampaignPageBody } from './TextCampaignPageBody';
import { useComposerBackHandler } from './hooks/useComposerBackHandler';

interface RouteParams {
	draftId: string;
}

export function TextingCampaignDraftFilterPage() {
	const { draftId: automationTemplateId } = useParams<RouteParams>();
	const userSession = useUserSession();
	const account = userSession?.account;
	const canSendFileLinks =
		isFilesFeatureEnabled(account) &&
		!!account?.features?.files?.canSendFileLinks &&
		!!userSession?.user?.userGroupPermissions?.featurePermissions?.files?.sendDownloadLink;
	const canRequestFiles =
		isFilesFeatureEnabled(account) && !!userSession?.user?.userGroupPermissions?.featurePermissions?.files?.requestFile;
	const location = useLocation<{
		initialContactIdsToOmit?: string[];
		initialContactIdsToInclude?: string[];
		initialFilterCriteriaList?: Api.IContactFilterCriteria[];
		initialSendOnBehalf?: Api.ISendOnBehalfPermission;
		initialSendFromUserId?: string;
		initialOwnershipFilter?: Api.IOwnershipFilter;
		initialCampaignTitle?: string;
		initialContentEditorState?: Api.IRichContentEditorState;
		initialAttachmentFiles?: File[];
		initialStoredAttachments?: Api.IFileAttachment[];
	}>();
	const [isEditingTitle, setIsEditingTitle] = useState(false);
	const titleInputRef = useRef<HTMLInputElement>(null);
	// Focus input when entering edit mode
	useEffect(() => {
		if (isEditingTitle && titleInputRef.current) {
			titleInputRef.current.focus();
			titleInputRef.current.select();
		}
	}, [isEditingTitle, titleInputRef]);

	const fullscreenModal = useFullscreenModal();
	const errorMessages = useErrorMessages();
	const onRequestBack = useComposerBackHandler();
	const automationTemplateIdQuery = useAutomationTemplateIdQuery({
		id: automationTemplateId,
	});
	const {
		state,
		dispatch,
		contactFilterQuery,
		contactIdsToOmit,
		contactsToAdd,
		contactApproximateQuery,
		effectiveFilterCriteriaList,
		onChangeFilterList,
		selectedOwnershipFilter,
		setSelectedOwnershipFilter,
		sendEmailFrom,
		sendEmailFromUser,
		onSendOnBehalf,
		totalRecipients,
		totalRecipientsWithTextingCapability,
	} = useTextingCampaignRecipientFilter({
		initialContactIdsToOmit: location.state?.initialContactIdsToOmit,
		initialFilterCriterias: location.state?.initialFilterCriteriaList,
		initialContactIdsToInclude: location.state?.initialContactIdsToInclude,
		initialSendOnBehalf: location.state?.initialSendOnBehalf,
		initialOwnershipFilter: location.state.initialOwnershipFilter,
		onAllFiltersRemoved: () => {
			// Clearing every filter sends the user back to pick recipients again. Replace rather than push:
			// an unfiltered filter page is the state we just navigated away from, not a back destination.
			fullscreenModal.history.replace({
				pathname: `/texting-campaign/template-search/${automationTemplateId}`,
				state: {
					initialSendOnBehalf: sendEmailFrom,
					initialOwnershipFilter: selectedOwnershipFilter,
					initialSendFromUserId: sendEmailFromUser?.id ?? location.state?.initialSendFromUserId,
				},
			});
		},
	});
	const { state: editorState, dispatch: editorDispatch } = useTextingCampaignEditor({
		initialCampaignTitle: location.state?.initialCampaignTitle,
		initialContentEditorState: location.state?.initialContentEditorState,
		initialAttachmentFiles: location.state?.initialAttachmentFiles,
		initialStoredAttachments: location.state?.initialStoredAttachments,
	});
	const didHydrateSendAsLink = useRef(false);
	useEffect(() => {
		if (didHydrateSendAsLink.current) {
			return;
		}
		const textingStep = automationTemplateIdQuery.data?.draft?.steps?.find(
			(step: Api.IAutomationStep) => step._type === 'TextingAutomationStep'
		) as Api.TextingAutomationStep | undefined;
		if (textingStep) {
			didHydrateSendAsLink.current = true;
			editorDispatch({ type: 'setSendAttachmentsAsLink', value: !!textingStep.sendAttachmentsAsLink });
		}
	}, [automationTemplateIdQuery.data, editorDispatch]);
	const tagSearchCriteria = Api.VmUtils.sortContactFilterCriteria(state.filterCriteriaList).searches.filter(x =>
		Api.VmUtils.isTagSearchContactFilterCriteria(x)
	);
	const onFilterOptionChanged = (selectedOption: Api.IOwnershipFilter) => {
		setSelectedOwnershipFilter(selectedOption);
	};
	const handleSaveCriteria = (
		filterCriteria: Api.IContactFilterCriteria[],
		_householdGrouping?: boolean,
		ownershipFilter?: Api.IOwnershipFilter
	) => {
		onChangeFilterList(filterCriteria, ownershipFilter);
		dispatch({ type: 'setShowingAdvancedTagFiltersModal', value: false });
	};
	const onNextButtonClick = () => {
		const fileSendError = getFileSendBlockingError({
			content: editorState.contentEditorState.getRawRichTextContent(),
			hasAttachments: editorState.attachmentFiles.length > 0 || editorState.storedAttachments.length > 0,
			sendAttachmentsAsLink: editorState.sendAttachmentsAsLink,
		});
		if (fileSendError) {
			errorMessages.push({ messages: [fileSendError] });
			return;
		}
		// Recipient edits and the composer only live in this page's local state, so stamp them onto the entry
		// we're leaving; otherwise the header's back arrow returns to the filters as they were on arrival.
		fullscreenModal.history.replace(location.pathname, {
			...location.state,
			initialContactIdsToOmit: Array.from(state.contactIdsToOmit),
			initialContactIdsToInclude: Array.from(state.contactIdsToAdd),
			initialFilterCriteriaList: state.filterCriteriaList,
			initialOwnershipFilter: selectedOwnershipFilter,
			initialSendOnBehalf: sendEmailFrom,
			initialSendFromUserId: sendEmailFromUser?.id ?? location.state?.initialSendFromUserId,
			initialCampaignTitle: editorState.campaignTitle,
			initialContentEditorState: editorState.contentEditorState,
			initialAttachmentFiles: editorState.attachmentFiles,
			initialStoredAttachments: editorState.storedAttachments,
		});
		fullscreenModal.history.push(`/texting-campaign/schedule-send/${automationTemplateId}`, {
			request: {
				filter: {
					criteria: effectiveFilterCriteriaList,
				},
				ownershipFilter: selectedOwnershipFilter,
				excludeContactIds: Array.from(state.contactIdsToOmit),
				includeContactIds: Array.from(state.contactIdsToAdd),
				sendFromOptions: {
					mode: sendEmailFrom?.sender?.mode,
					selectedUser: sendEmailFromUser?.id ?? location.state?.initialSendFromUserId,
					connectionType: sendEmailFrom?.sender?.connectionType,
				},
				autoStart: false,
				putOnHold: false,
				skipIfInProgress: true,
				skipIfInPrevious: false,
				groupByHousehold: false,
			},
		});
	};
	// Draft save mutation
	const saveDraftMutation = useTextingCampaignDraftUpdateMutation({
		onSuccess: () => {
			toast.success('Draft data saved successfully');
			invalidateAllAutomationTemplateIdQueries();
			setIsEditingTitle(false);
		},
		onError: error => {
			toast.error(error.systemMessage || 'Failed to save draft data');
		},
	});

	// Save draft function
	const handleSaveDraft = () => {
		if (!automationTemplateIdQuery.data?.draft?.steps) {
			return;
		}

		const currentDraftStep = automationTemplateIdQuery.data.draft.steps.find(
			(step: Api.IAutomationStep) => step._type === 'TextingAutomationStep'
		) as Api.TextingAutomationStep;

		if (!currentDraftStep) {
			toast.error('Unable to find texting content to save');
			return;
		}

		const request: Api.CreateTextCampaignAutomationRequest = {
			campaignName: automationTemplateIdQuery.data.name || '',
			content: editorState.contentEditorState.getRawRichTextContent(),
			attachments: editorState.storedAttachments || [],
			sendAttachmentsAsLink: editorState.sendAttachmentsAsLink,
			filter: {
				criteria: state.filterCriteriaList, // Save the current filter criteria
			},
			ownershipFilter: selectedOwnershipFilter,
			includeContactIds: contactsToAdd.map(c => c.data?.id).filter(Boolean) as string[],
			excludeContactIds: contactIdsToOmit,
			autoStart: false,
			putOnHold: false,
			skipIfInProgress: true,
			skipIfInPrevious: false,
			sendFromOptions: sendEmailFrom?.sender,
			groupByHousehold: false,
		};

		saveDraftMutation.mutateAsync({
			templateId: automationTemplateId,
			request,
		});
	};
	const handleSaveTitle = () => {
		// Don't save if title hasn't changed
		if (editorState.campaignTitle.trim() === automationTemplateIdQuery.data.name) {
			setIsEditingTitle(false);
			return;
		}
		// Get current draft content to preserve it during title update
		const currentDraftStep = automationTemplateIdQuery.data.draft?.steps?.[0];

		const request: Api.CreateTextCampaignAutomationRequest = {
			campaignName: editorState.campaignTitle.trim(),
			content: currentDraftStep.content,
			autoStart: false,
			putOnHold: false,
			skipIfInProgress: true,
			skipIfInPrevious: false,
			sendFromOptions: { mode: 'CurrentUser' as const },
			includeContactIds: [] as string[],
			excludeContactIds: [] as string[],
			attachments: (currentDraftStep as Api.TextingAutomationStep)?.attachments || [],
			sendAttachmentsAsLink: (currentDraftStep as Api.TextingAutomationStep)?.sendAttachmentsAsLink,
			groupByHousehold: false,
		};
		saveDraftMutation.mutate({
			templateId: automationTemplateId,
			request,
		});
	};
	const handleCancelEditTitle = () => {
		setIsEditingTitle(false);
		// Reset to original value
		if (automationTemplateIdQuery.data?.name) {
			editorDispatch({
				type: 'setCampaignTitle',
				value: location.state.initialCampaignTitle,
			});
		}
	};
	const handleTitleKeyPress = (event: React.KeyboardEvent) => {
		if (event.key === 'Enter') {
			event.preventDefault();
			handleSaveTitle();
		} else if (event.key === 'Escape') {
			event.preventDefault();
			handleCancelEditTitle();
		}
	};
	const renderRecipientItem = (contact: Api.IContact) => {
		let companyLine = '';
		if (contact.companyName != null) {
			companyLine = contact.jobTitle != null ? `${contact.jobTitle} at ${contact.companyName}` : contact.companyName;
		}
		const nameLabel = getDisplayNameForBulkEmail(contact);
		return (
			<div
				className={css(
					bs.flex,
					bs.itemsCenter,
					bs.border0,
					bs.borderB,
					bs.borderSolid,
					bs.borderGray200,
					bs.cursorPointer,
					bs.py5,
					bs.px0,
					bs.relative
				)}
				key={contact.id}
			>
				<DeprecatedCloseButton
					onClick={ev => {
						ev.stopPropagation();
						if (contact.id) {
							dispatch({ type: 'removeRecipient', value: contact });
						}
					}}
				/>

				<Avatar
					styles={[styleSheet.recipientsListItemAvatar]}
					fallbackStyles={[styleSheet.recipientsListItemAvatar]}
					imgSrc={contact.profilePic}
					fallbackText={getPrincipalInitials(contact)}
				/>

				<div className={css(bs.flex1, bs.textSm, bs.ml4, bs.overflowHidden)}>
					<div className={css(styleSheet.recipientsListItemDetailsName)}>{nameLabel}</div>
					<div className={css(styleSheet.recipientsListItemDetailsCompanyName, bs.truncateText)}>{companyLine}</div>
				</div>
				{!contact.canReceiveText ? <PhoneMissingPill styleDeclarations={[bs.absolute, bs.bottom2, bs.right2]} /> : null}
			</div>
		);
	};
	return (
		<>
			<MultiContainerHeader
				fullscreenHeader='New Texting Campaign'
				onFullscreenRequestBack={onRequestBack}
				onFullscreenRequestClose={() => fullscreenModal.dismissModal()}
			/>
			<CampaignComposerBaseLayout
				mainHeader={
					<div className={css(bs.px3, bs.py5, bs.flex, bs.gap2, bs.flexCol)}>
						<div className={css(bs.mb2)}>
							{automationTemplateIdQuery.status === 'success' ? (
								<div className={css(bs.flex, bs.itemsCenter, bs.gap2)}>
									{isEditingTitle ? (
										<div className={css(bs.flex, bs.itemsCenter, bs.gap2, bs.flex1)}>
											<div className={css(bs.flex, bs.flexCol, bs.flex1)}>
												<input
													ref={titleInputRef}
													type='text'
													value={editorState.campaignTitle}
													onChange={ev =>
														editorDispatch({
															type: 'setCampaignTitle',
															value: ev.target.value,
														})
													}
													onKeyDown={handleTitleKeyPress}
													className={css(
														bs.flex1,
														bs.textLg,
														bs.fontBold,
														bs.border,
														bs.borderSolid,
														bs.rounded,
														bs.px2,
														bs.py1,
														bs.bgWhite
													)}
													placeholder='Campaign title'
												/>
											</div>
											<Button
												variant='primary'
												size='sm'
												onClick={handleSaveTitle}
												disabled={saveDraftMutation.isPending || !editorState.campaignTitle.trim()}
												isLoading={saveDraftMutation.isPending}
											>
												Save
											</Button>
											<Button
												variant='link'
												size='sm'
												onClick={handleCancelEditTitle}
												disabled={saveDraftMutation.isPending}
											>
												Cancel
											</Button>
										</div>
									) : (
										<div className={css(bs.flex, bs.itemsCenter, bs.gap2, bs.flex1)}>
											<h2 className={css(bs.m0)}>{automationTemplateIdQuery.data?.name}</h2>
											<Button variant='link' size='icon' onClick={() => setIsEditingTitle(true)} styles={[bs.p1]}>
												<EditItemPenIcon fillColor={successGreen} />
											</Button>
										</div>
									)}
								</div>
							) : (
								<LoadingSpinner type='small' />
							)}
						</div>
						<SendFromDropdown
							disabled={!userSession.canSendOnBehalf}
							sendFromOption={sendEmailFrom}
							sendFromUserId={sendEmailFromUser?.id ?? location.state.initialSendFromUserId}
							canSendOnBehalf={userSession.canSendOnBehalf}
							allowedContactBasedOptions='all'
							sendOnBehalfOf={onSendOnBehalf}
						/>
					</div>
				}
				sideBar={
					<>
						<div className={css(styleSheet.recipientsHeader)}>
							<div className={css(styleSheet.recipientsHeaderTitle)}>
								<div>Recipients</div>
								<button
									className={css(bs.brandLink)}
									onClick={() =>
										dispatch({
											type: 'setShowingAdvancedTagFiltersModal',
											value: true,
										})
									}
								>
									<div>Advanced Tag Filter</div>
								</button>
								<FlyoutModal
									isOpen={state.showingAdvancedTagFiltersModal}
									onRequestClose={() =>
										dispatch({
											type: 'setShowingAdvancedTagFiltersModal',
											value: false,
										})
									}
									useDefaultHeader={false}
								>
									<AdvancedFiltersContextProvider
										initialFilterCriteria={state.filterCriteriaList}
										initialOwnershipFilter={selectedOwnershipFilter}
										initialSelectedUserId={sendEmailFromUser?.id ?? location.state?.initialSendFromUserId}
										onSave={handleSaveCriteria}
										refresh={state.showingAdvancedTagFiltersModal}
									>
										<AdvancedFiltersLegacyUI
											onDismissFlyout={() =>
												dispatch({
													type: 'setShowingAdvancedTagFiltersModal',
													value: false,
												})
											}
											showOwnershipFilters
											disableOwnershipFilters={sendEmailFrom?.sender?.mode === Api.SendEmailFrom.ContactOwner}
											showStatusFilters
											showKeyFactFilters
											showCustomFields
										/>
									</AdvancedFiltersContextProvider>
								</FlyoutModal>
							</div>
							<div className={css(bs.horizontalStack)}>
								{tagSearchCriteria.length > 0 ? (
									<OverflowAccordion
										threshold={65}
										onRenderTrigger={isExpanded => {
											return (
												<>
													<span className={css(bs.mr2)}>{`${isExpanded ? 'Hide' : 'Show all'} tags`}</span>
													<DisclosureIcon
														className={css(isExpanded ? styleSheet.tagPointUp : styleSheet.tagPointDown)}
													/>
												</>
											);
										}}
									>
										<ContactsTableActiveSearchesList
											hideAdvancedSearch={true}
											hideLabel={true}
											hideRemoveButtons={false}
											onSearchesChanged={(searches: Api.IContactFilterCriteria[]) => {
												const sorted = Api.VmUtils.sortContactFilterCriteria(state.filterCriteriaList);
												onChangeFilterList(sorted.filters.concat(searches));
											}}
											searches={tagSearchCriteria}
											tokenContainerStyles={[styleSheet.tagTokenContainer]}
											styles={[bs.hAuto, bs['-ml2']]}
										/>
									</OverflowAccordion>
								) : null}
							</div>
							<OwnershipFilterDropdown
								initialOwnershipFilter={selectedOwnershipFilter}
								onOwnershipFilterChanged={onFilterOptionChanged}
								disabled={sendEmailFrom?.sender?.mode === Api.SendEmailFrom.ContactOwner}
								allowSelectAUserOption={false}
								selectedUserId={sendEmailFromUser?.id ?? location.state?.initialSendFromUserId}
							/>
							<div className={css(bs.mt2, bs.flex, bs.itemsCenter, bs.flexWrap, bs.textSm, bs.justifyStart)}>
								<div className={css(bs.flex, bs.itemsCenter, bs.mr2)}>
									{contactApproximateQuery.isPending ? <LoadingSpinner type='tiny' /> : null}
									{totalRecipientsWithTextingCapability != null && totalRecipients != null ? (
										<>
											<span>
												{`${totalRecipientsWithTextingCapability} out of ${totalRecipients} contact${
													totalRecipients !== 1 ? 's' : ''
												} with phone numbers`}
											</span>
											<Tooltip>
												<TooltipTrigger>
													<InfoIcon fillColor={brandSecondary} className={css(bs.ml1)} />
												</TooltipTrigger>
												<TooltipContent>
													Excludes contacts that opted out of texting or cannot receive a text message.
												</TooltipContent>
											</Tooltip>
										</>
									) : null}
								</div>
							</div>
							<AutoCompleteSearchField
								anchorClassName='email-recipients-list-autocomplete-search-field-anchor'
								className='email-recipients-list-autocomplete-search-field'
								clearSearchFieldAfterSelectingItem={true}
								dropdownContentClassName={css(bs.wFull)}
								inputId='email-recipients-list-search-input'
								inputProps={{
									placeholder: 'Add additional contacts',
								}}
								leftAccessory={<SearchIcon className='email-recipients-list-autocomplete-search-field-icon' />}
								onItemSelected={(contact: Api.IContact) => {
									if (contact.id) {
										dispatch({ type: 'addRecipient', value: contact });
									}
								}}
								resultsLimit={6}
								type={Api.ResourceAutoCompleteViewModelType.Contact}
							/>
						</div>
						<div className={css(bs.flex, bs.flex1, bs.flexCol, bs.overflowXHidden, bs.overflowYAuto)}>
							<div>
								{contactsToAdd.map((contactQuery, i) => {
									if (!contactQuery.data) {
										return <LoadingSpinner key={i} type='small' />;
									}
									return renderRecipientItem(contactQuery.data);
								})}
								{contactFilterQuery.status === 'success' ? (
									<>
										{contactFilterQuery.data.pages.map(group => {
											return group.values
												.filter(contact => {
													if (!contact.id) {
														return false;
													}
													return (
														contactIdsToOmit.indexOf(contact.id) === -1 &&
														state.contactIdsToAdd.indexOf(contact.id) === -1
													);
												})
												.map(contact => {
													return renderRecipientItem(contact);
												});
										})}
										{contactFilterQuery.hasNextPage && !contactFilterQuery.isFetchingNextPage ? (
											<Waypoint onEnter={() => contactFilterQuery.fetchNextPage()} bottomOffset='-200px' />
										) : null}
									</>
								) : null}
							</div>
						</div>
						<div className={css(bs.py2, bs.px4, bs.border0, bs.borderT, bs.borderSolid, bs.borderGray200)}>
							<Checkbox
								id='textingCampaignAgreement-checkbox'
								className={css(bs.mb6)}
								checked={state.consentChecked}
								onChange={() => dispatch({ type: 'toggleConsentChecked' })}
							>
								<span className={css(bs.ml2)}>
									I agree that the recipients have opted in to receive texts and this is not a marketing campaign.
								</span>
							</Checkbox>
							<button
								className={css(bs.ctaButton, bs.wFull)}
								onClick={onNextButtonClick}
								disabled={!totalRecipientsWithTextingCapability || !state.consentChecked}
							>
								<span>Next: Send Options</span>
							</button>
						</div>
					</>
				}
			>
				<>
					<div
						className={css(
							bs.flex,
							bs.itemsCenter,
							bs.justifyEnd,
							bs.py3,
							bs.px5,
							bs.gap2,
							bs.border0,
							bs.borderB,
							bs.borderSolid,
							bs.borderMentionDark
						)}
					>
						<Button
							variant='secondary'
							disabled={saveDraftMutation.isPending}
							onClick={handleSaveDraft}
							isLoading={saveDraftMutation.isPending}
						>
							Save Draft
						</Button>
					</div>
					<div
						className={css(
							bs.itemsCenter,
							bs.boxBorder,
							bs.flex,
							bs.flex1,
							bs.flexCol,
							bs.overflowAuto,
							bs.py8,
							bs.px6,
							bs.bgGray50
						)}
					>
						<div className={css(bs.flex, bs.flexCol, bs.maxWXl, bs.wFull, bs.mxAuto)}>
							<TextCampaignPageBody
								contentEditorState={editorState.contentEditorState}
								attachmentFiles={editorState.attachmentFiles}
								storedAttachments={editorState.storedAttachments}
								sendAttachmentsAsLink={editorState.sendAttachmentsAsLink}
								onSendAttachmentsAsLinkChange={value => editorDispatch({ type: 'setSendAttachmentsAsLink', value })}
								canSendFileLinks={canSendFileLinks}
								canRequestFiles={canRequestFiles}
								onAttachFiles={files => editorDispatch({ type: 'addAttachmentFiles', value: files })}
								onRemoveAttachmentFile={file => editorDispatch({ type: 'removeAttachmentFile', value: file })}
								onRemoveStoredAttachment={attachmentId =>
									editorDispatch({
										type: 'removeStoredAttachment',
										value: attachmentId,
									})
								}
								onContentStateChanged={content =>
									editorDispatch({
										type: 'setTextingContentEditorState',
										value: content,
									})
								}
							/>
						</div>
					</div>
				</>
			</CampaignComposerBaseLayout>
		</>
	);
}
